/**
 * Utility functions for handling API errors and extracting meaningful error messages
 */

export interface ApiErrorResponse {
  message?: string;
  error?: string;
  detail?: string;
  non_field_errors?: string | string[];
  [key: string]: any;
}

export interface ApiError {
  response?: {
    status?: number;
    data?: ApiErrorResponse | string;
  };
  message?: string;
}

/**
 * Extracts a user-friendly error message from an API error response
 * Handles various error structures from different API frameworks
 * 
 * @param error - The error object from an API call
 * @param fallbackMessage - Default message if no specific error is found
 * @returns User-friendly error message
 */
export const extractErrorMessage = (
  error: ApiError,
  fallbackMessage: string = "An error occurred"
): string => {
  console.error("Original error object:", error);
  
  // Start with fallback message
  let errorMessage = fallbackMessage;
  
  if (error.response?.data) {
    const data = error.response.data;
    
    // Handle string response data
    if (typeof data === 'string') {
      errorMessage = data;
    }
    // Handle object response data with various structures
    else if (typeof data === 'object') {
      // Common API error message fields
      if (data.message) {
        errorMessage = data.message;
      } else if (data.error) {
        errorMessage = data.error;
      } else if (data.detail) {
        errorMessage = data.detail;
      } else if (data.non_field_errors) {
        // Django REST framework validation errors
        errorMessage = Array.isArray(data.non_field_errors)
          ? data.non_field_errors[0]
          : data.non_field_errors;
      } else {
        // Handle field-specific validation errors
        const fieldErrors: string[] = [];
        for (const [field, messages] of Object.entries(data)) {
          if (Array.isArray(messages)) {
            const message = messages[0];
            if (typeof message === 'string') {
              fieldErrors.push(`${field}: ${message}`);
            }
          } else if (typeof messages === 'string') {
            fieldErrors.push(`${field}: ${messages}`);
          }
        }
        
        if (fieldErrors.length > 0) {
          errorMessage = fieldErrors.join(', ');
        }
      }
    }
  } 
  // Fallback to error message if no response data
  else if (error.message) {
    errorMessage = error.message;
  }
  
  // Add HTTP status code for additional context
  if (error.response?.status) {
    errorMessage = `${errorMessage} (Status: ${error.response.status})`;
  }
  
  return errorMessage;
};

/**
 * Formats field validation errors into user-friendly messages
 * 
 * @param errors - Object containing field errors
 * @returns Array of formatted error messages
 */
export const formatFieldErrors = (errors: Record<string, string | string[]>): string[] => {
  const formattedErrors: string[] = [];
  
  for (const [field, messages] of Object.entries(errors)) {
    if (Array.isArray(messages)) {
      messages.forEach(message => {
        formattedErrors.push(`${field}: ${message}`);
      });
    } else if (typeof messages === 'string') {
      formattedErrors.push(`${field}: ${messages}`);
    }
  }
  
  return formattedErrors;
};

/**
 * Checks if an error is a network/connection error
 * 
 * @param error - The error object
 * @returns True if it's a network error
 */
export const isNetworkError = (error: ApiError): boolean => {
  return !error.response && (error.message?.includes('Network Error') ?? false);
};

/**
 * Checks if an error is a timeout error
 * 
 * @param error - The error object
 * @returns True if it's a timeout error
 */
export const isTimeoutError = (error: ApiError): boolean => {
  return (error.message?.includes('timeout') || error.message?.includes('ETIMEDOUT')) ?? false;
};
